import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { HapticProvider } from "@/components/HapticProvider";
import CookieConsent from "@/components/CookieConsent";
import { MAINTENANCE_MODE } from "@/config/maintenance";
import Maintenance from "./pages/Maintenance";
import Index from "./pages/Index";

const Lineage = lazy(() => import("./pages/Lineage"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Research = lazy(() => import("./pages/Research"));
const ResearchArticle = lazy(() => import("./pages/ResearchArticle"));
const CareerDetail = lazy(() => import("./pages/CareerDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Workers = lazy(() => import("./pages/Workers"));
const WorkersDashboard = lazy(() => import("./pages/WorkersDashboard"));
const BlogEditor = lazy(() => import("./pages/BlogEditor"));
const ResearchEditor = lazy(() => import("./pages/ResearchEditor"));
const VerifyApplication = lazy(() => import("./pages/VerifyApplication"));
const Teams = lazy(() => import("./pages/Teams"));
const TeamsInternal = lazy(() => import("./pages/TeamsInternal"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  // Show maintenance page when enabled
  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <HapticProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/lineage" element={<Lineage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/research" element={<Research />} />
              <Route path="/research/:slug" element={<ResearchArticle />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/careers/:slug" element={<CareerDetail />} />
              <Route path="/jobs" element={<Navigate to="/careers" replace />} />
              <Route path="/jobs/verify" element={<VerifyApplication />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/workers" element={<Workers />} />
              <Route path="/workers/dashboard" element={<WorkersDashboard />} />
              <Route path="/workers/editor/blog/:id" element={<BlogEditor />} />
              <Route path="/workers/editor/research/:id" element={<ResearchEditor />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/teams/internal" element={<TeamsInternal />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<Cookies />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <CookieConsent />
            </BrowserRouter>
          </HapticProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
